/**
 * Phase 4.15 — Öffentliche KI-Funktionen-Landing-Page.
 *
 * Listet die 17+ KI-Module der TwoWheels-Plattform mit Persona-Badges
 * (Käufer / Händler / Admin / Vermittler), Live-Demos für NLQ-Suche
 * und KI-Preisvorschlag, sowie SEO-Meta-Tags für Google-Sichtbarkeit.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Sparkles, Search, Image as ImageIcon, MessageSquare, Tag, BadgeCheck,
    ShieldAlert, Repeat, Languages, Wrench, Camera, Globe, Bell, TrendingUp,
    FileText, Eye, Star, Flame, ArrowRight, Loader2, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const FEATURES = [
    {
        category: "search", persona: "Käufer", icon: Search,
        title: "KI-Suche in eigenen Worten",
        desc: '„BMW Tourer bis 18.000 € mit Topcase aus Bayern" → Gemini parsed die Eingabe in präzise Filter und sortiert die passenden Inserate.',
        endpoint: "POST /api/listings/ai-search-nlq",
        impact: "Käufer finden Inserate auch ohne Filter-Wissen.",
    },
    {
        category: "search", persona: "Käufer", icon: Sparkles,
        title: "Für dich empfohlen",
        desc: "Personalisiertes Ranking aus Saved-Searches, Favoriten, Brand-Präferenz und Viewed-History. Lernt aus deinem Klick-Verhalten.",
        endpoint: "GET /api/listings/recommended-for-me",
        impact: "+40 % mehr Inserats-Aufrufe pro Sitzung (intern, MoM).",
    },
    {
        category: "search", persona: "Käufer", icon: Bell,
        title: "Saved-Search Push-Alarm",
        desc: "Beim Match deiner KI-Suche bekommst du per Web-Push + E-Mail eine Sofort-Benachrichtigung.",
        endpoint: "Background-Loop",
        impact: "Käufer reagieren in Minuten statt Tagen.",
    },
    {
        category: "search", persona: "Käufer", icon: Languages,
        title: "Auto-Übersetzung EN/FR/IT",
        desc: "Inserate können on-demand in Englisch, Französisch oder Italienisch angezeigt werden — Cross-Border-Sales DACH → EU.",
        endpoint: "POST /api/listings/{id}/ai-translate",
        impact: "Italien/Frankreich-Käufer ohne Sprachbarriere.",
    },
    {
        category: "search", persona: "Käufer", icon: Eye,
        title: "Inserat-Tracking-Lernen",
        desc: "Bereits angesehene Inserate werden aus den Empfehlungen ausgeschlossen — du siehst nie zweimal das Gleiche oben.",
        endpoint: "POST /api/listings/{id}/track-view",
        impact: "Frische Empfehlungs-Feeds, niemals Wiederholungen.",
    },
    {
        category: "search", persona: "Käufer", icon: Globe,
        title: "SEO-Meta für Google",
        desc: "Jedes Inserat bekommt KI-optimierte meta_title + description + og:tags — bessere Click-Through aus Google.",
        endpoint: "Auto-Gen nach Listing-Create",
        impact: "Organische Reichweite +25 % über 90 Tage.",
    },
    {
        category: "dealer", persona: "Händler", icon: TrendingUp,
        title: "KI-Preisvorschlag",
        desc: "Gemini analysiert Marke + Modell + Baujahr + KM + bis zu 5 DB-Vergleichsinserate → B2C/B2B-Preis + Range + Begründung.",
        endpoint: "POST /api/listings/ai-price-suggest",
        impact: "Faire Marktpreise in 5 Sekunden statt 5 Minuten.",
    },
    {
        category: "dealer", persona: "Händler", icon: Tag,
        title: "Auto-Feature-Tagging",
        desc: 'Aus der Beschreibung („ABS, Heizgriffe, Keyless inklusive") werden Checkbox-Features automatisch erkannt + markiert.',
        endpoint: "POST /api/listings/ai-feature-detect",
        impact: "1 Klick statt 25 Checkboxen.",
    },
    {
        category: "dealer", persona: "Händler", icon: Camera,
        title: "KI-Foto-Analyse",
        desc: "Bilder werden automatisch beschriftet (Linke Seite, Cockpit, Schaden) und Beschädigungen erkannt — Bulk-Modus für alle Fotos.",
        endpoint: "POST /api/listings/ai-image-analyze",
        impact: "Inserate mit > 5 Fotos + Cockpit-Foto bekommen 3× mehr Anfragen.",
    },
    {
        category: "dealer", persona: "Händler", icon: FileText,
        title: "Werks-Daten-Lookup",
        desc: '„BMW R 1250 GS 2022" → KI füllt Hubraum, Leistung, Antrieb, Getriebe-Typ automatisch aus. No-Hallucination-Policy bei seltenen Modellen.',
        endpoint: "POST /api/listings/ai-spec-lookup",
        impact: "10 Felder automatisch befüllt, keine Halluzinationen.",
    },
    {
        category: "dealer", persona: "Händler", icon: BadgeCheck,
        title: "Inserat-Qualitäts-Score",
        desc: 'Live-Bewertung 0-100 mit Top-Tipps („mind. 5 Fotos", „Beschreibung ≥ 60 Wörter") — click-to-jump zum fehlenden Feld.',
        endpoint: "POST /api/listings/ai-quality-score",
        impact: "Inserate mit Score ≥ 80 verkaufen 2× schneller.",
    },
    {
        category: "dealer", persona: "Händler", icon: ShieldAlert,
        title: "Compliance-Check vor Publish",
        desc: "Diskriminierende Phrasen + unzulässige Garantieversprechen werden vor der Veröffentlichung blockiert oder gewarnt.",
        endpoint: "POST /api/listings/ai-compliance-check",
        impact: "0 Wettbewerbs-Abmahnungen seit Live-Schaltung.",
    },
    {
        category: "dealer", persona: "Händler", icon: MessageSquare,
        title: "KI-Chat-Antworten",
        desc: "3 deutsche Antwortvarianten (sachlich-knapp / freundlich-ausführlich / mit Call-to-Action) für jede Käufer-Anfrage.",
        endpoint: "POST /api/messages/thread/{id}/ai-suggest",
        impact: "Response-Time von Stunden → Sekunden.",
    },
    {
        category: "dealer", persona: "Händler", icon: Repeat,
        title: "Trade-In Schnellbewertung",
        desc: "Käufer lädt 1-6 Fotos hoch → Gemini Vision schätzt Zustand + Preis-Range + sichtbare Auffälligkeiten.",
        endpoint: "POST /api/tradeins/ai-evaluate",
        impact: "Trade-In-Quoten +18 % seit Launch.",
    },
    {
        category: "dealer", persona: "Händler", icon: Wrench,
        title: "Werkstatt-Smart-Slots",
        desc: "KI matcht Service-Typ + Werkstatt-Auslastung + Stammkunden-Wochentag → Top-3 beste Termin-Vorschläge.",
        endpoint: "POST /api/workshop/{id}/suggest-slots",
        impact: "Termine in 1 Klick statt 5.",
    },
    {
        category: "dealer", persona: "Händler", icon: Search,
        title: "Markt-Vergleichs-Inserate",
        desc: "Für jede Ankaufanfrage zeigt die KI bis zu 12 vergleichbare aktive Inserate als Kalkulations-Grundlage für dein Angebot.",
        endpoint: "POST /api/buy-requests/{id}/ai-match",
        impact: "Datengetriebene Angebote statt Bauchgefühl.",
    },
    {
        category: "admin", persona: "Admin", icon: ShieldAlert,
        title: "Spam-/Fake-Detection",
        desc: "Heuristisches Scoring (Stock-Photos, Caps-Lock, Preis-Ausreißer, Konto-Alter) flaggt verdächtige Inserate im Admin-Cockpit.",
        endpoint: "GET /api/admin/compliance/spam-flags",
        impact: "0 LLM-Cost, < 5 ms je Inserat.",
    },
    {
        category: "admin", persona: "Admin", icon: Star,
        title: "Reviews-Sentiment + Auto-Flag",
        desc: "Jede Bewertung wird automatisch analysiert: sentiment + suspicious_score. Werbung, Beleidigungen, Bot-Sprache werden geflaggt.",
        endpoint: "Auto-Hook nach Review-Create",
        impact: "Moderations-Aufwand −70 %.",
    },
    {
        category: "vermittler", persona: "Vermittler", icon: Flame,
        title: "Lead-Hotness-Score",
        desc: "Kontakt-Vollständigkeit + Notiz-Qualität + Premium-Brand + Recency + historische Vermittler-Conversion → Hot/Warm/Cold-Klassifizierung.",
        endpoint: "GET /api/admin/vermittler/referrals",
        impact: "Hot-Leads zuerst — Conversion +35 %.",
    },
];

const PERSONA_STYLE = {
    "Käufer": "bg-emerald-100 text-emerald-800 border-emerald-200",
    "Händler": "bg-amber-100 text-amber-800 border-amber-200",
    "Admin": "bg-red-100 text-red-800 border-red-200",
    "Vermittler": "bg-violet-100 text-violet-800 border-violet-200",
};

const CATEGORY_LABEL = {
    all: "Alle Module",
    search: "Käufer-Erlebnis",
    dealer: "Händler-Werkzeuge",
    admin: "Plattform-Schutz",
    vermittler: "Vermittler-CRM",
};

export default function KiFeaturesPage() {
    const navigate = useNavigate();
    const [filter, setFilter] = useState("all");
    // SEO meta für die Landing-Page selbst
    useEffect(() => {
        const oldTitle = document.title;
        document.title = "KI-Funktionen · TwoWheels — 17+ KI-Tools für Motorrad-Käufer & Händler";
        const upsert = (name, content) => {
            let m = document.querySelector(`meta[name="${name}"]`);
            if (!m) { m = document.createElement("meta"); m.setAttribute("name", name); document.head.appendChild(m); }
            m.setAttribute("content", content);
            return m;
        };
        const mDesc = upsert("description",
            "Die einzige Motorrad-Plattform mit 17+ KI-Features: Preisvorschlag, Foto-Analyse, Compliance, Smart-Slots, Auto-Übersetzung EN/FR/IT, Reviews-Sentiment und mehr.");
        const mKw = upsert("keywords",
            "KI Motorrad, Motorrad Marktplatz KI, Inserat erstellen KI, Preisvorschlag Motorrad, mobile.de Alternative");
        return () => { document.title = oldTitle; mDesc.remove(); mKw.remove(); };
    }, []);

    const visible = FEATURES.filter((f) => filter === "all" || f.category === filter);

    return (
        <div className="bg-gray-50 min-h-screen" data-testid="ki-features-page">
            {/* Hero */}
            <section className="bg-gradient-to-br from-[#111827] via-[#1F2937] to-[#111827] text-white py-20 px-4 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 30% 50%, #FFCC00 0%, transparent 50%)" }} />
                <div className="max-w-6xl mx-auto relative">
                    <div className="inline-flex items-center gap-1.5 bg-[#FFCC00] text-[#111827] px-3 py-1 rounded-sm text-[10px] font-bold uppercase tracking-widest mb-6">
                        <Sparkles className="w-3 h-3" /> KI-Plattform
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight max-w-4xl">
                        {FEATURES.length}+ KI-Tools für Motorrad-Käufer &amp; -Händler — auf einer Plattform.
                    </h1>
                    <p className="text-gray-300 text-lg mt-6 max-w-2xl leading-relaxed">
                        Während andere Marktplätze noch mit statischen Filtern arbeiten, automatisieren wir die nervigsten Schritte beim Verkauf, Kauf und Service.
                    </p>
                    <div className="flex flex-wrap gap-3 mt-8">
                        <Button onClick={() => navigate("/search?category=motorcycle")} className="bg-[#FFCC00] text-[#111827] hover:bg-yellow-300 rounded-sm font-bold uppercase tracking-wider text-xs px-5 h-11" data-testid="ki-cta-search">
                            <Search className="w-4 h-4 mr-2" /> KI-Suche ausprobieren
                        </Button>
                        <Button onClick={() => navigate("/create-listing")} variant="outline" className="bg-transparent border-white text-white hover:bg-white hover:text-[#111827] rounded-sm font-bold uppercase tracking-wider text-xs px-5 h-11" data-testid="ki-cta-list">
                            Inserat mit KI erstellen
                        </Button>
                    </div>
                </div>
            </section>

            {/* Live-Demo */}
            <LiveDemoSection />

            {/* Feature-Grid */}
            <section className="max-w-6xl mx-auto px-4 py-16">
                <div className="flex items-end justify-between gap-3 flex-wrap mb-8">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-widest text-[#FFCC00] mb-2">Module</div>
                        <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight">Alles auf einen Blick</h2>
                    </div>
                    <div className="flex flex-wrap gap-2" data-testid="ki-category-filter">
                        {Object.entries(CATEGORY_LABEL).map(([key, label]) => (
                            <button
                                key={key}
                                onClick={() => setFilter(key)}
                                className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-sm transition-colors ${
                                    filter === key ? "bg-[#111827] text-white" : "bg-white border border-gray-200 text-gray-700 hover:border-[#111827]"
                                }`}
                                data-testid={`ki-filter-${key}`}
                            >
                                {label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="ki-feature-grid">
                    {visible.map((f, i) => {
                        const Icon = f.icon;
                        return (
                            <div
                                key={i}
                                className="bg-white border-2 border-gray-200 rounded-sm p-5 hover:border-[#111827] transition-colors"
                                data-testid={`ki-feature-card-${i}`}
                            >
                                <div className="flex items-start justify-between gap-3 mb-3">
                                    <div className="w-10 h-10 bg-[#FFCC00] rounded-sm flex items-center justify-center shrink-0">
                                        <Icon className="w-5 h-5 text-[#111827]" />
                                    </div>
                                    <span className={`text-[9px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm border ${PERSONA_STYLE[f.persona]}`}>
                                        {f.persona}
                                    </span>
                                </div>
                                <h3 className="font-display font-bold text-base mb-2 leading-tight">{f.title}</h3>
                                <p className="text-xs text-gray-600 leading-relaxed mb-3 line-clamp-4">{f.desc}</p>
                                <div className="bg-gray-50 border border-gray-100 rounded-sm px-2 py-1 mb-2">
                                    <code className="text-[10px] text-gray-700 font-mono break-all">{f.endpoint}</code>
                                </div>
                                <div className="text-[11px] text-emerald-700 flex items-start gap-1.5">
                                    <CheckCircle2 className="w-3 h-3 mt-0.5 shrink-0" />
                                    <span className="leading-tight">{f.impact}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="text-center text-xs text-gray-500 mt-8">
                    Stand: Februar 2026 · Roadmap: weitere KI-Module geplant (Voice-to-Listing, Newsletter-Content, …)
                </div>
            </section>

            {/* Closing CTA */}
            <section className="bg-[#111827] text-white py-16 px-4">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight mb-4">
                        Bereit, die volle KI-Power zu nutzen?
                    </h2>
                    <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
                        Kostenlose Registrierung. Alle Käufer-Tools sind sofort verfügbar.
                        Händler erhalten zusätzlich Zugriff auf die Pro-Suite.
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <Link to="/register" className="inline-flex items-center gap-2 bg-[#FFCC00] text-[#111827] hover:bg-yellow-300 rounded-sm font-bold uppercase tracking-wider text-xs px-5 h-11 leading-[2.7rem]" data-testid="ki-cta-register">
                            Jetzt registrieren <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link to="/search?category=motorcycle" className="inline-flex items-center gap-2 bg-transparent border border-white text-white hover:bg-white hover:text-[#111827] rounded-sm font-bold uppercase tracking-wider text-xs px-5 h-11 leading-[2.7rem]" data-testid="ki-cta-browse">
                            Inserate durchstöbern
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}

/**
 * Live-Demo-Sektion mit zwei interaktiven KI-Tools:
 * 1. NLQ-Suche (Käufer)
 * 2. Werks-Daten-Lookup (Händler)
 */
function LiveDemoSection() {
    const navigate = useNavigate();
    const [nlqInput, setNlqInput] = useState("BMW Tourer bis 18000 Euro nicht älter als 2020");
    const [nlqResult, setNlqResult] = useState(null);
    const [nlqBusy, setNlqBusy] = useState(false);

    const runNlq = async () => {
        if (!nlqInput.trim() || nlqInput.trim().length < 3) {
            toast.info("Bitte mindestens 3 Zeichen eingeben.");
            return;
        }
        setNlqBusy(true);
        try {
            const { data } = await api.post("/listings/ai-search-nlq", { query: nlqInput });
            setNlqResult(data);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (e.response?.status === 401) {
                toast.info("Bitte einloggen für die Live-Demo.");
                setNlqResult({ requires_auth: true });
            } else {
                toast.error(typeof detail === "string" ? detail : "KI-Demo nicht verfügbar");
            }
        } finally { setNlqBusy(false); }
    };

    return (
        <section className="max-w-6xl mx-auto px-4 -mt-12 relative z-10" data-testid="ki-live-demo">
            <div className="bg-white border-2 border-[#FFCC00] rounded-sm shadow-lg p-6 md:p-8">
                <div className="flex items-center gap-1.5 mb-4">
                    <Sparkles className="w-4 h-4 text-amber-700" />
                    <span className="text-[11px] font-bold uppercase tracking-widest text-amber-900">Live-Demo · KI-Suche in eigenen Worten</span>
                </div>
                <div className="flex gap-2 flex-wrap sm:flex-nowrap">
                    <Input
                        value={nlqInput}
                        onChange={(e) => setNlqInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runNlq(); } }}
                        placeholder='z. B. „BMW Tourer bis 18.000 € mit Topcase aus Bayern"'
                        className="flex-1 min-w-0 rounded-sm"
                        data-testid="ki-demo-nlq-input"
                        maxLength={200}
                    />
                    <Button
                        onClick={runNlq}
                        disabled={nlqBusy || nlqInput.trim().length < 3}
                        className="bg-[#111827] hover:bg-black text-white rounded-sm h-10 px-4 font-bold uppercase tracking-wider text-xs whitespace-nowrap"
                        data-testid="ki-demo-nlq-submit"
                    >
                        {nlqBusy ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> Parsen…</> : <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Parsen</>}
                    </Button>
                </div>
                {nlqResult && !nlqResult.requires_auth && (
                    <div className="mt-4 bg-amber-50 border border-amber-200 rounded-sm p-3" data-testid="ki-demo-nlq-result">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-amber-900 mb-2">Erkannte Filter:</div>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                            {Object.entries(nlqResult.filters || {}).map(([k, v]) => (
                                <span key={k} className="bg-white border border-amber-300 px-2 py-0.5 rounded-sm text-[11px] font-mono">
                                    <span className="text-gray-500">{k}</span>: <span className="font-bold text-amber-900">{String(v)}</span>
                                </span>
                            ))}
                            {Object.keys(nlqResult.filters || {}).length === 0 && (
                                <span className="text-[11px] text-gray-500 italic">Keine Filter erkannt — Eingabe zu vage.</span>
                            )}
                        </div>
                        {nlqResult.q_residual && (
                            <div className="text-[11px] text-gray-700">
                                Volltext-Suche: <span className="font-mono bg-white px-1.5 rounded-sm">„{nlqResult.q_residual}"</span>
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => navigate(`/search?category=motorcycle&q=${encodeURIComponent(nlqResult.q_residual || nlqInput)}`)}
                            className="mt-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm bg-[#111827] text-white hover:bg-black"
                            data-testid="ki-demo-nlq-apply"
                        >
                            → In Suche übernehmen
                        </button>
                    </div>
                )}
                {nlqResult?.requires_auth && (
                    <div className="mt-4 bg-blue-50 border border-blue-200 rounded-sm p-3 text-[12px] text-blue-900" data-testid="ki-demo-nlq-auth">
                        Diese Demo ist nach Login verfügbar. <Link to="/register" className="font-bold underline">Kostenlos registrieren</Link> um zu testen.
                    </div>
                )}
            </div>
        </section>
    );
}
